import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TAppThunk, TRootState } from 'slices/store';
import { parseCustomerSignature } from 'utils/customerSignature';
import { type ITEToken, TE_ROOT_USER, type TTEUser } from '@timeedit/types';
import api from '../services/api.service';
import { fetchAssignablePermissions } from './assignablePermission.slice';
import { fetchOrganization } from './organization.slice';

type TAuthState = {
  loading: boolean;
  hasErrors: boolean;
  user: ITEToken | null;
  myUser: TTEUser | null;
};

const initialState: TAuthState = {
  loading: true,
  hasErrors: false,
  user: null,
  myUser: null,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    onLoginSuccess: (state, { payload: user }: PayloadAction<ITEToken>) => {
      if (!user) {
        /**
         * If we don't have a user, this should be considered an error
         * and we'll consequently set the redux state to mimic this
         */
        state.hasErrors = true;
        state.user = null;
        state.myUser = null;
      } else {
        state.hasErrors = false;
        state.user = user;
      }
      state.loading = false;
    },
    onLogout: (state) => {
      state.hasErrors = false;
      state.loading = false;
      state.user = null;
      state.myUser = null;
    },
    onFetchTEUserSuccess: (
      state,
      { payload: teUser }: PayloadAction<TTEUser>
    ) => {
      state.myUser = teUser;
    },
  },
});

export default slice.reducer;

// Selectors
export const userSelector: (state: TRootState) => ITEToken = (
  state: TRootState
) => state.auth.user;
/** Use only for URL & navigation related purposes */
export const parsedCustomerSignatureSelector: (state: TRootState) => string = (
  state: TRootState
) => parseCustomerSignature(state.auth.user.customerSignature);
export const myUserSelector: (state: TRootState) => TTEUser = (
  state: TRootState
) => state.auth.myUser;

export const selectUserIsRoot = (state: TRootState) =>
  state.auth.user?.scopes?.includes(TE_ROOT_USER) ?? false;

// Actions
export const { onLoginSuccess, onLogout, onFetchTEUserSuccess } = slice.actions;

export const fetchTEUserFromToken =
  (token: ITEToken): TAppThunk =>
  async (dispatch) => {
    try {
      const endpoint = token.scopes.includes('TE_ROOT_USER')
        ? `/users/root/${token.id}`
        : `/users/${token.id}`;
      const response = await api.get<unknown, TTEUser>({
        endpoint,
        suppressNotification: true,
      });
      dispatch(onFetchTEUserSuccess(response));
    } catch (error) {
      console.error(error);
    }
  };

export const fetchInitialData = (): TAppThunk => (dispatch) => {
  dispatch(fetchOrganization());
  dispatch(fetchAssignablePermissions());
};
